import { getApolloContext } from "@apollo/client";
import gql from "graphql-tag";
import React, { useState, useEffect, useContext } from "react";

const GROUPS = gql`
  query Groups {
    groups {
      _id
      competition {
        _id
        name
      }
      group
      position
      matchesWon
      matchesLost
      matchesDrawn
      matchesPlayed
      points
      goalDifference
      goalsShot
      goalsConceded
      team {
        name
        code
      }
    }
  }
`;

const GROUPS_BY_COMPETITION = gql`
  query Groups($competition: String) {
    groupsByCompetition(competition: $competition) {
      _id
      competition {
        _id
        name
      }
      group
      position
      matchesWon
      matchesLost
      matchesDrawn
      matchesPlayed
      points
      goalDifference
      goalsShot
      goalsConceded
      team {
        name
        code
      }
    }
  }
`;

const COMPETITIONS = gql`
  query Competitions {
    competitions {
      _id
      name
      description
      opta_ID
      formation
      code
      country
      country_code
      competition_type
      badge
    }
  }
`;

const GAMES = gql`
  query Games {
    games {
      _id
      time
      description
      date
      group
      fifa_position
      fifa_result
      fifa_home_stats
      fifa_away_stats
      round
      round_type
      home_scorers
      away_scorers
      home_stats
      away_stats
      competition {
        _id
        name
        badge
      }
      home_team {
        _id
        name
        short_name
        code
      }
      away_team {
        _id
        name
        short_name
        code
      }
      live_data
    }
  }
`;

const GAMES_BY_DATE = gql`
  query Games($date: String) {
    gamesByDate(date: $date) {
      _id
      time
      description
      date
      fifa_position
      fifa_result
      home_stats
      away_stats
      fifa_home_stats
      fifa_away_stats
      stadium {
        opta_name
      }
      competition {
        name
        badge
      }
      lineup {
        home {
          squad {
            _id
            shirt_number
            opta_shirt_number
            player {
              _id
              first_name
              last_name
              first_name_upper
              last_name_upper
              nationality {
                _id
                opta_code
                opta_name
              }
            }
          }
          order
          yellow_card
          dbl_yellow_card
          red_card
        }
        away {
          squad {
            _id
            shirt_number
            opta_shirt_number
            player {
              _id
              first_name
              last_name
              first_name_upper
              last_name_upper
              nationality {
                _id
                opta_code
                opta_name
              }
            }
          }
          order
          yellow_card
          dbl_yellow_card
          red_card
        }
      }
      opta_lineup {
        home {
          squad {
            _id
            opta_shirt_number
            player {
              first_name
              last_name
            }
          }
          order
        }
        away {
          squad {
            _id
            opta_shirt_number
            player {
              first_name
              last_name
            }
          }
          order
        }
      }
      home_team {
        _id
        name
        short_name
        code
      }
      away_team {
        _id
        name
        short_name
        code
      }
      live_data
      home_stats
      away_stats
    }
  }
`;

const UPDATE_GAME = gql`
  mutation UpdateGame(
    $id: String!
    $live_data: JSON
    $lineup: JSON
    $home_scorers: JSON
    $away_scorers: JSON
    $home_penalties: JSON
    $away_penalties: JSON
    $home_stats: JSON
    $away_stats: JSON
  ) {
    updateGame(
      id: $id
      live_data: $live_data
      lineup: $lineup
      home_scorers: $home_scorers
      away_scorers: $away_scorers
      home_penalties: $home_penalties
      away_penalties: $away_penalties
      home_stats: $home_stats
      away_stats: $away_stats
    ) {
      live_data
    }
  }
`;

const SQUAD_BY_TEAM = gql`
  query Squad($teamId: String, $seasonId: String) {
    squadByTeam(teamId: $teamId, seasonId: $seasonId) {
      _id
      active
      type
      shirt_number
      opta_shirt_number
      position
      player {
        _id
        opta_ID
        first_name
        last_name
        first_name_upper
        last_name_upper
        title
        nationality {
          _id
          opta_code
          opta_name
        }
      }
      team {
        name
        short_name
      }
    }
  }
`;

const APIContext = React.createContext([{}, () => {}]);

const APIProvider = (props) => {
  const { client } = useContext(getApolloContext());
  let serverAddress = window.dataUrl.replace("ws", "http");

  if (serverAddress.slice(-1) === "/") {
    serverAddress = serverAddress.slice(0, -1);
  }

  function getSeason(seasonId) {
    return fetch(serverAddress + "/seasons/" + (seasonId ? seasonId : ""))
      .then((response) => response.json())
      .then((data) => {
        return data;
      });
  }
  function getOfficial(officialId) {
    return fetch(serverAddress + "/officials/" + (officialId ? officialId : ""))
      .then((response) => response.json())
      .then((data) => {
        return data;
      });
  }
  function getStadium(stadiumId) {
    return fetch(serverAddress + "/stadiums/" + (stadiumId ? stadiumId : ""))
      .then((response) => response.json())
      .then((data) => {
        return data;
      });
  }

  function getCompetition(compId) {
    return client
      .query({
        query: COMPETITIONS,
      })
      .then((response) => {
        return response.data.competitions;
      })
      .catch((err) => console.error(err));
  }

  function getGroup() {
    return client
      .query({
        query: GROUPS,
        fetchPolicy: "network-only",
      })
      .then((response) => {
        return response.data.groups;
      })
      .catch((err) => console.error(err));
  }

  function getGroupByCompetition(competition) {
    return client
      .query({
        query: GROUPS_BY_COMPETITION,
        fetchPolicy: "network-only",
        variables: { competition: competition },
      })
      .then((response) => {
        return response.data.groupsByCompetition;
      })
      .catch((err) => {
        console.error(err);
      });
  }

  function updatePlayer(player) {
    return fetch(serverAddress + "/players/" + player._id, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(player),
    });
  }
  function updateLineup({ data, fixture, team }) {
    return fetch(serverAddress + "/games/" + fixture._id, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        $set: { [`live_data.${team}_team_lineup`]: data },
      }),
    });
  }

  function getSquads() {
    return fetch(serverAddress + "/squads/")
      .then((response) => response.json())
      .then((data) => {
        return data;
      });
  }

  function getTeams({ team }) {
    return fetch(serverAddress + "/teams/" + (team || "?$orderby=name"))
      .then((response) => response.json())
      .then((data) => {
        return data;
      });
  }

  function getFixture({ fixtureId }) {
    return client
      .query({
        query: GAMES,
      })
      .then((response) => {
        return response.data.games.find((game) => game._id === fixtureId);
      })
      .catch((err) => console.error(err));
  }

  function getFixtures({ competition, date }) {
    return client
      .query({
        query: GAMES_BY_DATE,
        variables: { date: date + "Z" },
      })
      .then((response) => {
        return response.data.gamesByDate;
      })
      .catch((err) => console.error(err));
  }

  function getAllFixtures({ competition, date }) {
    return client
      .query({
        query: GAMES,
        fetchPolicy: "network-only",
      })
      .then((response) => {
        return response.data.games;
      })
      .catch((err) => console.error(err));
  }

  function updateGame({
    id,
    live_data,
    lineup,
    home_scorers,
    away_scorers,
    home_penalties,
    away_penalties,
    home_stats,
    away_stats,
  }) {
    return client
      .mutate({
        mutation: UPDATE_GAME,
        variables: {
          id,
          live_data,
          lineup,
          home_scorers,
          away_scorers,
          home_penalties,
          away_penalties,
          home_stats,
          away_stats,
        },
      })
      .then((response) => {
        return response.data.updateGame;
      })
      .catch((err) => console.error(err));
  }

  function getSquadPlayers({ teamId, seasonId, coach }) {
    return client
      .query({
        query: SQUAD_BY_TEAM,
        fetchPolicy: "network-only",
        variables: {
          teamId: teamId,
          seasonId: seasonId,
        },
      })
      .then((response) => {
        return response.data.squadByTeam;
      })
      .catch((err) => console.error(err));
  }
  function getSquad({ teamId, seasonId }) {
    return getSquadPlayers({ teamId: teamId, seasonId: seasonId });
  }

  function getLineup({ fixtureId, team }) {
    return getFixture({ fixtureId: fixtureId }).then((fixture) => {
      let team_lineup = fixture.live_data[`${team}_team_lineup`];
      let url = "";

      return getSquadPlayers({
        teamId: fixture[team + "_team"],
        seasonId: fixture.season,
      }).then((squadPlayers) => {
        if (team_lineup && team_lineup.length > 1) {
          url =
            serverAddress +
            "/players/?$orderby=last_name&$filter=_id $in " +
            team_lineup.map((d) => d.player).join(",");
        } else if (team_lineup && team_lineup.length > 0) {
          url = serverAddress + "/players/" + team_lineup[0].player;
        }

        if (url === "") {
          return Promise.resolve();
        }

        return fetch(url)
          .then((response) => response.json())
          .then((data) => {
            if (Array.isArray(data)) {
              return team_lineup.map((l) => {
                return {
                  ...l,
                  ...squadPlayers.find((p) => p.player === l.player),
                  ...data.find((p) => p._id === l.player),
                  order: l.order,
                };
              });
            } else {
              return team_lineup.map((l) => {
                return { ...l, ...data, order: l.order };
              });
            }
          });
      });
    });
  }

  function getPlayersBySearch({ search, count, limit, page }) {
    let url =
      serverAddress + "/players?$filter=last_name $regex '^" + search + "'";
    if (count) {
      url += "&$count";
    }
    if (limit) {
      url += "&$limit=" + limit;
    }
    if (page) {
      url += "&$skip=" + page;
    }

    return fetch(url)
      .then((response) => response.json())
      .then((data) => {
        return data;
      });
  }

  function createFixture({ fixture }) {
    let newFixture = {
      ...fixture,
      live_data: { home_score: 0, away_score: 0 },
    };
    return fetch(serverAddress + "/games/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(newFixture),
    });
  }

  function deleteFixture({ fixtureId }) {
    return fetch(serverAddress + "/games/" + fixtureId, {
      method: "DELETE",
    });
  }

  function getOfficial(officialId) {
    return fetch(serverAddress + "/officials/" + (officialId || ""))
      .then((response) => response.json())
      .then((data) => {
        return data;
      });
  }

  function updateGameOfficial({ fixtureId, official, type }) {
    return fetch(serverAddress + "/games/" + fixtureId, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        $set: { [type]: official._id },
      }),
    });
  }

  function updateHeadCoach({ teamId, coach }) {
    return fetch(serverAddress + "/teams/" + teamId, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        $set: { coach: coach },
      }),
    });
  }
  function getPlayer({ playerId }) {
    return fetch(serverAddress + "/players/" + (playerId || ""))
      .then((response) => response.json())
      .then((data) => {
        return data;
      });
  }

  function updatePlayer(player) {
    if (player._id) {
      player = { ...player, nationality: player.nationality._id };
      return fetch(serverAddress + "/players/" + player._id, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(player),
      });
    } else {
      return fetch(serverAddress + "/players/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(player),
      });
    }
  }
  return (
    <APIContext.Provider
      value={{
        updatePlayer,
        updateLineup,
        getLineup,
        getFixture,
        getTeams,
        getSquad,
        getPlayersBySearch,
        getSquads,
        createFixture,
        deleteFixture,
        getOfficial,
        updateGameOfficial,
        updateHeadCoach,
        getCompetition,
        getSeason,
        getStadium,
        getOfficial,
        getPlayer,
        getFixtures,
        updatePlayer,
        updateGame,
        getGroup,
        getAllFixtures,
        getGroupByCompetition,
      }}
    >
      {props.children}
    </APIContext.Provider>
  );
};
export { APIContext, APIProvider };
